import { IComments } from '@/models/Comments'
import { CommentsService } from '@/service/CommentsService'
import { GAEventTypes, trackCustomGAEvents } from '@ghl/clientportal-core'
import { reactive } from 'vue'
import { GroupState } from './groups'
import { PostState } from './posts'
import { UserState } from './users'
export class CommentsState {
  state = reactive({
    comments: [],
    fetching: false,
    postComments: new Map<string, IComments[]>(),
    commentLoading: new Map<string, boolean>(),
    postCommentsNextPage: new Map<string, string>(), //map<parentId,previousId>
  })

  async fetchCommentById(postId: string, commentId: string, parentId?: string): Promise<IComments | undefined> {
    if (parentId) {
      // fetch the parent comment first
      await this.fetchCommentById(postId, parentId)
      // fetching other child comments
      await this.fetchComments(postId, { parentId, limit: 10 })
    }
    if (!parentId) {
      parentId = postId
    }
    const comments = this.state.postComments.get(parentId)
    //find the comment in the array and return if it exist
    const commentIndex = comments?.findIndex(comment => comment._id === commentId)
    if (commentIndex != undefined && commentIndex !== -1) {
      return comments![commentIndex]
    }

    this.state.commentLoading.set(parentId, true)
    try {
      const comment = await CommentsService.fetchCommentById(postId, commentId)

      // add the comment to list if not already present
      if (!this.state.postComments.has(parentId)) {
        this.state.postComments.set(parentId, [comment])
      } else {
        const comments = this.state.postComments.get(parentId)

        //if comment already exist dont add it
        const commentIndex = comments?.findIndex(commentInArray => commentInArray._id === comment._id)
        if (commentIndex === -1) {
          comments?.push(comment)
        }
        this.state.postComments.set(parentId, comments as IComments[])
      }
      this.state.commentLoading.set(parentId, false)
      return comment
    } catch (error) {
      this.state.commentLoading.set(parentId, false)
      console.error(error)
    }
  }

  highlightComment(postId: string, commentId: string, parentId?: string, addHighlight = true): void {
    // A deep link's `parentId` equals `postId` when the highlighted comment
    // is itself a top-level comment (see `commentCard.helper.ts` fallback to
    // `parentPostId`). Treat those as top-level, not as replies.
    const isTopLevelTarget = !parentId || parentId === postId

    if (parentId && !isTopLevelTarget) {
      // Highlighted target is a reply — recurse to expand its parent's replies.
      this.highlightComment(postId, parentId, undefined, false)
    }

    const threadKey = isTopLevelTarget ? postId : (parentId as string)
    const comments = this.state.postComments.get(threadKey)
    let targetIndex = -1
    // Reset any previously highlighted/expanded state, and locate the target.
    comments?.forEach((comment, index) => {
      if (comment._id === commentId) {
        targetIndex = index
      }
      if (comment.isHighlighted) {
        comment.isHighlighted = false
      }
      if (comment.expandReplies) {
        comment.expandReplies = false
      }
    })

    if (targetIndex === -1 || !comments) {
      return
    }

    if (isTopLevelTarget && addHighlight) {
      // Top-level deep link (from a notification/share link). The comment may
      // have been fetched via `fetchCommentById` and pushed to the end of the
      // list even though it's older — promote it to the top so the user sees
      // it immediately without scrolling through the first page.
      const [comment] = comments.splice(targetIndex, 1)
      comments.unshift(comment)
      comment.isHighlighted = true
      return
    }

    // Reply target (or recursive parent expansion): don't reorder the list —
    // just mark the comment so `CommentCard.vue` can scroll it into view.
    const target = comments[targetIndex]
    if (!addHighlight) {
      // Recursive call for the parent of a highlighted reply — expand its
      // replies so the child becomes visible.
      target.expandReplies = true
    } else {
      target.isHighlighted = true
    }
  }

  unhighlightComment(postId: string, commentId: string, parentId?: string): void {
    if (parentId) {
      this.unhighlightComment(postId, parentId, undefined)
    }

    if (!parentId) {
      parentId = postId
    }
    const comments = this.state.postComments.get(parentId)
    //find the comment in the array and shift it to start of array
    const commentIndex = comments?.findIndex(comment => comment._id === commentId)
    if (commentIndex !== undefined && commentIndex !== -1 && comments) {
      comments.at(commentIndex)!.isHighlighted = false
    }
  }

  async fetchComments(postId: string, query?: any): Promise<IComments[] | undefined> {
    let newComments: IComments[] = []

    const parentId = query?.parentId ? query.parentId : postId
    if (!query?.previousId && this.state.postComments.has(parentId)) {
      if (query?.limit) {
        newComments = this.state.postComments.get(parentId) as []
        return newComments.slice(0, query.limit)
      } else {
        return this.state.postComments.get(parentId)
      }
    }

    this.state.commentLoading.set(parentId, true)
    // Ask the API for newest-first order when fetching top-level comments.
    // Replies are left in the API's default (chronological) order.
    const isTopLevel = !query?.parentId
    const serviceQuery = isTopLevel ? { sortOrder: 'DESC', ...query } : query
    newComments = await CommentsService.fetchComments(postId, serviceQuery)

    //Set comments parent and add array in map if not available
    if (!this.state.postComments.has(parentId)) {
      this.state.postComments.set(parentId, [])
    }

    //Append Comments in Map for specific Parent
    const comments = this.state.postComments.get(parentId)

    // check id the comments array id comment is already present do not add it
    newComments.forEach(comment => {
      const commentIndex = comments?.findIndex(commentInArray => commentInArray._id === comment._id)
      if (commentIndex === -1) {
        comments?.push(comment)
      }
    })
    this.state.postComments.set(parentId, comments as IComments[])

    //Add parentId in set if nextPage is available or delete it from set
    if (query?.limit && newComments.length == query.limit && comments) {
      const previousId = newComments[newComments.length - 1]._id
      this.state.postCommentsNextPage.set(parentId, previousId as string)
    } else {
      this.state.postCommentsNextPage.delete(parentId)
    }
    this.state.commentLoading.set(parentId, false)
    return this.state.postComments.get(parentId)
  }

  async createComment(data: IComments, channelSlug?: string): Promise<IComments> {
    const comment = await CommentsService.create(data)
    trackCustomGAEvents(GAEventTypes.COMMENT_CREATED, {
      postId: comment.postId,
      commentId: comment._id,
    })

    const userData = await UserState.getUserByContactId(UserState.state.currentUser?.contactId as string)

    comment.user = {
      fullName: UserState.state.currentUser?.fullName as string,
      avatar: UserState.state.currentUser?.avatar as string,
      slug: UserState.state.currentUser?.slug as string,
      contactId: UserState.state.currentUser?.contactId as string,
      gamificationMeta: userData?.gamificationMeta,
    }
    comment.current_user_reactions = {
      LIKE: false,
    }
    comment.reactions = {
      LIKE: 0,
    }
    const post = PostState.state.posts.find((post: any) => post._id === data.postId)
    const homeTimelinePinnedPost = PostState.state.pinnedPosts
      .get(GroupState.state.groupData.slug as string)
      ?.find((post: any) => post._id === data.postId)

    const parentId = comment?.parentId ? comment.parentId : data.postId
    if (this.state.postComments.has(parentId)) {
      const comments: IComments[] = this.state.postComments.get(parentId) as IComments[]
      //Checking if comment is Child Comment
      if (comment.parentId) {
        //Fetching Parent Comment to update repliesCount
        const postComments = this.state.postComments.get(data.postId) as IComments[]
        const parentCommentInd = postComments?.findIndex(postComment => {
          return comment.parentId === postComment._id
        })
        //Incrementing Replies Count in Parent Comment
        postComments[parentCommentInd].repliesCount += 1
        //Push child comment to end of thread
        comments?.push(comment)
      }
      //If it is parent comment directly linked to Post
      else {
        //Append parent comment at start of thread
        comments?.unshift(comment)
      }
      this.state.postComments.set(parentId, comments)
    } else {
      this.state.postComments.set(parentId, [comment])
    }

    //if post found
    if (post) {
      channelSlug = post.channelSlug
      post.commentsCount! += 1
      const userIndex = post.commentsAvatarGroupOption?.findIndex((option: any) => option.name === UserState.state.currentUser?.fullName)
      if (userIndex == -1) {
        if (comment.user?.fullName) {
          post.commentsAvatarGroupOption?.push(comment.user)
        }
      }
    }
    //if pinned post on home timeline found
    if (homeTimelinePinnedPost) {
      channelSlug = homeTimelinePinnedPost.channelSlug
      homeTimelinePinnedPost.commentsCount! += 1
    }

    if (channelSlug) {
      const channelTimelinePinnedPost = PostState.state.pinnedPosts.get(channelSlug)?.find((post: any) => post._id === data.postId)
      if (channelTimelinePinnedPost) {
        channelTimelinePinnedPost.commentsCount! += 1
      }
    }
    return comment
  }

  async editComment(postId: string, commentId: string, data: IComments): Promise<IComments> {
    return await CommentsService.updateComment(data, postId, commentId)
  }

  getParent(comment: IComments): string {
    const parentId = comment?.parentId ? comment.parentId : comment.postId
    return parentId
  }

  async deleteComment(commentPayload: any, channelSlug?: string): Promise<void> {
    try {
      const parentId = this.getParent(commentPayload)
      const commentThread = this.state.postComments.get(parentId) as IComments[]

      const commentIndex = commentThread.findIndex(comment => commentPayload._id === comment._id)
      if (commentIndex !== -1) {
        commentThread.splice(commentIndex, 1)
      }

      //Decrement comment count in state
      let postCounter = 1
      if (commentPayload?.parentId) {
        const commentThread = this.state.postComments.get(commentPayload.postId) as IComments[]
        const parentCommentInd = commentThread.findIndex(comment => commentPayload.parentId === comment._id)
        commentThread[parentCommentInd].repliesCount -= 1
      } else {
        postCounter += commentPayload.repliesCount
        this.state.postComments.delete(commentPayload._id)
      }
      const post = PostState.state.posts.find(post => commentPayload.postId === post._id)

      // if post is present in posts array the update comment count
      if (post?.commentsCount) {
        channelSlug = post.channelSlug
        post.commentsCount -= postCounter
      }

      // if post is present in pinned post array the update comment count
      const homeTimelinePinnedPost = PostState.state.pinnedPosts
        .get(GroupState.state.groupData.slug as string)
        ?.find(post => commentPayload.postId === post._id)

      if (homeTimelinePinnedPost?.commentsCount) {
        channelSlug = homeTimelinePinnedPost.channelSlug
        homeTimelinePinnedPost.commentsCount -= postCounter
      }

      // if post is present in pinned post array the update comment count
      if (channelSlug) {
        const channelTimelinePinnedPost = PostState.state.pinnedPosts
          .get(channelSlug)
          ?.find((post: any) => commentPayload.postId === post._id)
        if (channelTimelinePinnedPost) {
          channelTimelinePinnedPost.commentsCount! -= postCounter
        }
      }

      await CommentsService.deleteComment(commentPayload._id, commentPayload.postId, commentPayload.parentId)
    } catch (error) {
      console.error(error)
      throw error
    }
  }
}
export const CommentState = new CommentsState()
